import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";

const RANGES = [
  { key: "yesterday", label: "Yesterday" },
  { key: "current", label: "Current month" },
  { key: "last", label: "Last month" },
  { key: "3m", label: "Last 3 months" },
  { key: "6m", label: "Last 6 months" },
  { key: "custom", label: "Custom range" },
];

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function rangeFor(key, customFrom, customTo) {
  const today = new Date();
  if (key === "yesterday") {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { from: isoDate(y), to: isoDate(y) };
  }
  if (key === "current") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: isoDate(start), to: isoDate(today) };
  }
  if (key === "last") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: isoDate(start), to: isoDate(end) };
  }
  if (key === "3m") {
    const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    return { from: isoDate(start), to: isoDate(today) };
  }
  if (key === "6m") {
    const start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
    return { from: isoDate(start), to: isoDate(today) };
  }
  return { from: customFrom, to: customTo };
}

export default function ClientReports() {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [metricDefs, setMetricDefs] = useState([]);
  const [range, setRange] = useState("current");
  const [customFrom, setCustomFrom] = useState(isoDate(new Date()));
  const [customTo, setCustomTo] = useState(isoDate(new Date()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.client_id) loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (projectId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, range, customFrom, customTo]);

  async function loadProjects() {
    const { data } = await supabase
      .from("projects")
      .select("id, name, metric_keys")
      .eq("client_id", profile.client_id);
    setProjects(data || []);
    if (data && data.length > 0) setProjectId(data[0].id);
  }

  async function loadData() {
    setLoading(true);
    const project = projects.find((p) => p.id === projectId);
    const { data: allMetrics } = await supabase.from("master_metrics").select("*");
    const defs = (project?.metric_keys || []).map(
      (key) => (allMetrics || []).find((m) => m.key === key) || { key, display_name: key, unit: "count" }
    );
    setMetricDefs(defs);

    const { from, to } = rangeFor(range, customFrom, customTo);
    const { data } = await supabase
      .from("metrics")
      .select("*")
      .eq("project_id", projectId)
      .gte("date", from)
      .lte("date", to)
      .order("date", { ascending: false });

    const byDate = {};
    (data || []).forEach((row) => {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][row.metric_key] = row.value;
    });
    setRows(Object.values(byDate));
    setLoading(false);
  }

  const totals = {};
  rows.forEach((row) => {
    metricDefs.forEach((m) => {
      if (row[m.key] !== undefined) totals[m.key] = (totals[m.key] || 0) + Number(row[m.key]);
    });
  });

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Reports</h1>
      {projects.length > 1 && (
        <select
          value={projectId || ""}
          onChange={(e) => setProjectId(e.target.value)}
          className="text-sm border border-input rounded-lg px-3 py-1.5 mb-4 bg-background"
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`font-mono text-[11.5px] px-3 py-1.5 rounded-full border ${
              range === r.key ? "bg-primary text-primary-foreground border-primary" : "border-input text-muted-foreground"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {range === "custom" && (
        <div className="flex items-center gap-2 mb-4 border border-input rounded-lg px-3 py-2 w-fit">
          <span className="text-xs text-muted-foreground">From</span>
          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-sm bg-background" />
          <span className="text-xs text-muted-foreground">To</span>
          <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-sm bg-background" />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4 mb-5">
            {metricDefs.map((m) => (
              <div key={m.key} className="border border-border rounded-2xl bg-card p-4">
                <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                  {m.display_name}
                </div>
                <div className="text-lg font-semibold text-foreground">{formatValue(totals[m.key], m.unit)}</div>
              </div>
            ))}
          </div>

          <div className="border border-border rounded-2xl bg-card overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
              Daily breakdown
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground p-5">No data in this range yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left font-mono text-[10px] uppercase text-muted-foreground px-5 py-2.5">Date</th>
                    {metricDefs.map((m) => (
                      <th key={m.key} className="text-right font-mono text-[10px] uppercase text-muted-foreground px-5 py-2.5">
                        {m.display_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.date} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5">{row.date}</td>
                      {metricDefs.map((m) => (
                        <td key={m.key} className="px-5 py-2.5 text-right font-mono text-xs">
                          {row[m.key] !== undefined ? formatValue(row[m.key], m.unit) : "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatValue(value, unit) {
  const v = value || 0;
  if (unit === "currency") return `₹${Number(v).toLocaleString("en-IN")}`;
  if (unit === "percentage") return `${v}%`;
  if (unit === "ratio") return `${v}x`;
  return Number(v).toLocaleString("en-IN");
}
